import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getInternalEventImportExportTableDataRetrieveQueryKey,
  useInternalEventLoadCreate,
  useInternalEventTemplateListSuspense,
} from "gen/endpoints/internal/internal";
import { useUserCustomerListSuspense } from "gen/endpoints/users/users";
import { EventImportExportReadData } from "gen/model/eventImportExportReadData";
import { EventLoadCreateDataRequest } from "gen/model/eventLoadCreateDataRequest";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { rootRoute } from "shared/navigation/Routes";

export interface InternalTemplateLoadModalProps {
  onCloseComplete(): void;
}

export const InternalTemplateLoadModal: React.FC<
  InternalTemplateLoadModalProps
> = ({ onCloseComplete }) => {
  const { t } = useTranslation(["internal", "common"]);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const navigate = useNavigate();

  const { data: customerList } = useUserCustomerListSuspense();
  const { data: templateList } = useInternalEventTemplateListSuspense();
  const { mutateAsync: createEventTemplateLoad } = useInternalEventLoadCreate();

  const form = useForm<EventLoadCreateDataRequest>();
  const queryClient = useQueryClient();

  const onSubmit = async (data: EventLoadCreateDataRequest) => {
    const templateLoad = await createEventTemplateLoad({
      data,
    });
    await queryClient.invalidateQueries({
      queryKey: getInternalEventImportExportTableDataRetrieveQueryKey({
        import_export_type: "import",
      }),
    });
    return templateLoad;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <QueryBoundary>
          <Form
            form={form}
            onSubmit={onSubmit}
            onSuccess={(templateExportData: EventImportExportReadData) => {
              onClose();
              navigate(
                rootRoute({})
                  .internal({})
                  .shadowTemplate({
                    templateVersionId:
                      templateExportData.event_template_version_id,
                  })
                  .templateLoad({
                    templateImportExportId: templateExportData.id,
                  }).$,
              );
            }}
            successMessage={t("internal:templateTabs.loadTemplate.success")}
          >
            <ModalHeader>
              {t("internal:templateTabs.loadTemplate.title")}
              <ModalCloseButton />
            </ModalHeader>
            <ModalBody>
              <Text mb={4}>
                {t("internal:templateTabs.loadTemplate.description")}
              </Text>
              <FormSelect
                title={t("internal:customer")}
                name="linked_customer_id"
              >
                {customerList.map(({ id, name }) => (
                  <SelectOption value={id!} key={id}>
                    {name}
                  </SelectOption>
                ))}
              </FormSelect>
              <FormSelect
                title={t("internal:template")}
                name="event_template_id"
              >
                {templateList.map(({ event_template_id, name }) => (
                  <SelectOption
                    value={event_template_id}
                    key={event_template_id}
                  >
                    {name}
                  </SelectOption>
                ))}
              </FormSelect>
            </ModalBody>
            <ModalFooter gap={2}>
              <ButtonOrder
                dismiss={
                  <FormCancelButton onClick={onClose} autoFocus={true} />
                }
                confirm={
                  <FormSubmitButton>{t("common:submit")}</FormSubmitButton>
                }
              />
            </ModalFooter>
          </Form>
        </QueryBoundary>
      </ModalContent>
    </Modal>
  );
};
